/**
 * Authentication types for the valve control API
 * Defines all interfaces and types used in the authentication system
 */

import { User as ApiUser } from './api';

// Request/Response types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: ApiUser;
}

export interface AuthResult {
  success: boolean;
  user?: ApiUser;
  error?: string;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RefreshTokenResponse {
  access_token: string;
  refresh_token: string;
  expires_in: number;
}

// Token and user information
export interface TokenInfo {
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
  tokenType: string;
}

// Re-export the User type from api.ts for consistency
export type User = ApiUser;

// Event types for the event-driven authentication system
export type AuthEventType = 
  | 'authStateChanged'
  | 'tokensRefreshed'
  | 'authError'
  | 'tokenExpiring'
  | 'userUpdated';

export interface AuthEvent {
  type: AuthEventType;
  payload?: any;
  timestamp: number;
}

export interface AuthStateChangedEvent extends AuthEvent {
  type: 'authStateChanged';
  payload: {
    isAuthenticated: boolean;
    user?: User;
  };
}

export interface TokensRefreshedEvent extends AuthEvent {
  type: 'tokensRefreshed';
  payload: {
    accessToken: string;
    refreshToken: string;
    expiresAt: number;
  };
}

export interface AuthErrorEvent extends AuthEvent {
  type: 'authError';
  payload: {
    error: string;
    code?: string;
    context?: any;
  };
}

export interface TokenExpiringEvent extends AuthEvent {
  type: 'tokenExpiring';
  payload: {
    expiresIn: number;
  };
}

export interface UserUpdatedEvent extends AuthEvent {
  type: 'userUpdated';
  payload: {
    user: User;
  };
}

// Event listener type
export type AuthEventListener = (event: AuthEvent) => void;

// Authentication state
export interface AuthState {
  isAuthenticated: boolean;
  user: User | null;
  isLoading: boolean;
  error: string | null;
  // User management properties
  users: User[];
  usersLoading: boolean;
  usersError: string | null;
  userOperationLoading: boolean;
  userOperationError: string | null;
  userOperationSuccess: boolean;
  profileUpdateSuccess: boolean;
  profileUpdateError: string | null;
  // Additional properties needed by components
  signupLoading: boolean;
  signupError: string | null;
  profileUpdateLoading: boolean;
  passwordChangeLoading: boolean;
  passwordChangeError: string | null;
  passwordChangeSuccess: boolean;
  // Email confirmation and password reset states
  emailConfirmationSuccess: boolean;
  passwordResetSuccess: boolean;
  requestPasswordResetSuccess: boolean;
}

// API error types
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

// Configuration types
export interface AuthConfig {
  apiBaseUrl: string;
  tokenRefreshThreshold: number; // Seconds before expiration to refresh
  maxRetries: number;
  retryDelay: number; // Milliseconds
}

// Storage keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'auth_access_token',
  REFRESH_TOKEN: 'auth_refresh_token',
  TOKEN_EXPIRES_AT: 'auth_token_expires_at',
  USER_INFO: 'auth_user_info',
} as const;